// Ultimate Card Game (UCG) - Online 1v1 Lobby
//
// Quick Match (random queue) or a 6-character room code, both over
// js/net/wsMatchClient.js against the authoritative WebSocket game server
// (server/matchMaker.js). Once the server pairs two players it sends
// MATCH_START, which hands off to Game.startOnlineMatchWS() (js/main.js) -
// everything from there on (simulation, hidden hand, snapshots) runs
// server-side; this file only has to get a WSMatchClient connected.

import { DECKS_DATA } from '../data/decks.js';
import { loadCustomDecks } from '../data/customDecks.js';
import { WSMatchClient } from '../net/wsMatchClient.js';
import { buildNetworkDeckPayload, deriveBaseRace } from '../engine/cardSystem.js';
import { progress } from '../engine/progress.js';
import { MatchmakingBrawler } from './matchmakingBrawler.js';
import { getFactionCrestMarkup } from './factionCrestMarkup.js';
import { REEL_3_STYLES_MAP } from '../engine/slotMachine.js';
import { inventory } from '../engine/inventory.js';
import { showRecoverPrompt } from './accountOverlay.js';
import { showCallsignModal } from './callsignModal.js';
import { showLeaderboardModal, getRankTier } from './leaderboardModal.js';
import { getDisplayName, getMMR, addIdentityListener, fetchProfile, ensureIdentity, getAuth } from '../net/playerIdentity.js';
import { hubNavigator } from './hubNavigator.js';
import { notifyVersionMismatch } from './updateManager.js';

export class OnlineLobby {
  constructor(game) {
    this.game = game;
    this.modal = document.getElementById('online-modal');
    this.isOpen = false;
    this.waitPollTimer = null;
    this.queueStartTime = null;
    this.botFallbackThresholdSec = 15;
    this.currentCode = null;
    // 'normal' or 'blitz' (4x speed - the server itself ticks the match
    // faster, see server/matchRoom.js speedMultiplier) - chosen by tapping
    // one of the match tiles, which fires quickJoin() right away.
    this.mode = 'normal';
    // Selected deck id from the tile row (js/ui/deckBuilder.js's own
    // #deck-presets-list tile chassis, reused here as a single scrollable
    // row) - resolved in populateDeckTiles() on every openModal().
    this.selectedDeckId = null;
    this._pendingDuelShare = false;
    this._duelConfirmTimer = null;
    this._toastTimer = null;

    const brawlerCanvas = document.getElementById('online-brawler-canvas');
    const brawlerKills = document.getElementById('online-brawler-kills');
    this.brawler = brawlerCanvas ? new MatchmakingBrawler(brawlerCanvas, brawlerKills) : null;

    this.initListeners();
    this.checkUrlJoin();
  }

  initListeners() {
    document.getElementById('btn-open-online')?.addEventListener('click', () => this.openModal());
    // No dedicated close button: the header's DECK 01-04 tabs (index.html)
    // are the way out - HubNavigator.navigateTo() closes this modal.
    // Click-outside-closes on desktop - no separate backdrop div here, so
    // only a click landing on the container itself (not bubbled up from
    // .modal-box or its children) counts.
    this.modal?.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });
    // Match tiles: one tap picks the mode AND starts the search immediately
    // (no separate "Quick Match" confirm step anymore).
    for (const btn of document.querySelectorAll('.online-mode-btn')) {
      btn.addEventListener('click', () => {
        this.setMode(btn.dataset.mode);
        this.quickJoin();
      });
    }
    // Private Game tile: tap splits it into Create Link / Join; Create Link
    // launches straight away, Join reveals the code input inline.
    document.getElementById('online-private-toggle')?.addEventListener('click', () => this.openPrivateSplit());
    document.getElementById('btn-online-create')?.addEventListener('click', () => this.create());
    document.getElementById('online-private-join-half')?.addEventListener('click', () => this.openPrivateJoinForm());
    document.getElementById('btn-online-join')?.addEventListener('click', () => this.join());
    document.getElementById('btn-online-cancel')?.addEventListener('click', () => this.handleCancelClick());
    document.getElementById('btn-online-duel-share')?.addEventListener('click', () => this.handleDuelShareClick());
    document.getElementById('btn-online-bot-fallback')?.addEventListener('click', () => this.launchBotFallback());
    document.getElementById('btn-online-discord-matchup')?.addEventListener('click', () => {
      window.open('https://discord.gg/UECTSvDB4u', '_blank', 'noopener,noreferrer');
    });
    document.getElementById('btn-leave-online')?.addEventListener('click', () => this.leaveMatch());
    document.getElementById('btn-online-copy-code')?.addEventListener('click', () => this.copyCode());
    document.getElementById('btn-online-copy-link')?.addEventListener('click', () => this.copyLink());
    document.getElementById('btn-edit-callsign')?.addEventListener('click', () => {
      showCallsignModal({ game: this.game, onSaved: () => this.updateCommanderBanner() });
    });
    document.getElementById('btn-online-ladder')?.addEventListener('click', () => {
      showLeaderboardModal(this.game);
    });
    document.getElementById('btn-warning-recover')?.addEventListener('click', () => {
      showRecoverPrompt();
    });
    addIdentityListener(() => this.updateCommanderBanner());

    const codeInput = document.getElementById('online-code-input');
    if (codeInput) {
      codeInput.addEventListener('input', () => {
        codeInput.value = codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      });
      codeInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this.join();
      });
      const savedCode = localStorage.getItem('ucg_last_online_code');
      if (savedCode) codeInput.value = savedCode;
    }

    window.addEventListener('beforeunload', () => {
      this.wsClient?.leave?.();
    });
  }

  checkUrlJoin() {
    const params = new URLSearchParams(window.location.search);
    const joinCode = params.get('join');
    if (joinCode) {
      const cleanCode = joinCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (cleanCode.length >= 1 && cleanCode.length <= 6) {
        setTimeout(() => {
          this.openModal();
          const codeInput = document.getElementById('online-code-input');
          if (codeInput) codeInput.value = cleanCode;
          this.join(cleanCode);
        }, 300);
      }
    }
  }

  updateCommanderBanner() {
    const nameEl = document.getElementById('online-commander-name');
    const rankEl = document.getElementById('online-commander-rank');
    const mmrEl = document.getElementById('online-commander-mmr');
    if (nameEl) nameEl.textContent = getDisplayName();
    if (mmrEl) mmrEl.textContent = `${getMMR()} MMR`;
    if (rankEl) {
      const tier = getRankTier(getMMR());
      rankEl.innerHTML = `${tier.icon} ${tier.name}`;
      rankEl.style.color = tier.color;
    }

    const auth = getAuth();
    const isGuest = !auth || !auth.accountId || auth.isExpired;
    const warningEl = document.getElementById('online-guest-warning');
    if (warningEl) {
      warningEl.style.display = isGuest ? 'flex' : 'none';
    }
  }

  openModal() {
    this.isOpen = true;
    if (this.modal) this.modal.className = 'online-modal-container active';
    // HubNavigator.setActiveDeck() highlights tabs globally; online isn't a
    // stop on the rail, so none of this header's tabs should read as current.
    this.modal?.querySelectorAll('.deck-tab').forEach(tab => {
      tab.classList.remove('active');
      tab.setAttribute('aria-selected', 'false');
    });
    fetchProfile().catch(() => {});
    this.updateCommanderBanner();
    this.populateDeckTiles();
    this.setMode(this.mode);
    this.resetPrivateTile();
    this.showView('choice');
    this.setError('');
  }

  closeModal() {
    this.isOpen = false;
    this.resetDuelShareState();
    if (this.modal) this.modal.className = 'online-modal-container';
    this.stopWaitingPoll();
    this.brawler?.stop();
  }

  setMode(mode) {
    this.mode = mode === 'blitz' ? 'blitz' : (mode === '2v2' ? '2v2' : 'normal');
    for (const btn of document.querySelectorAll('.online-mode-btn')) {
      const active = btn.dataset.mode === this.mode;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    }
  }

  showView(name) {
    for (const view of ['choice', 'waiting', 'connecting']) {
      const el = document.getElementById(`online-view-${view}`);
      if (el) el.style.display = view === name ? '' : 'none';
    }
  }

  setError(message) {
    const el = document.getElementById('online-error');
    if (el) el.innerText = message;
  }

  _syncModeBadge() {
    const badge = document.getElementById('online-waiting-mode-badge');
    if (badge) {
      if (this.mode === 'blitz') {
        badge.textContent = 'BLITZ';
        badge.classList.remove('hidden');
      } else if (this.mode === '2v2') {
        badge.textContent = '2v2 DUO';
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    }
  }

  // Renders the deck tile row (same chassis as js/ui/deckBuilder.js's own
  // deck selector) and keeps this.selectedDeckId pointed at a valid deck -
  // called fresh on every openModal() since custom decks can change between
  // sessions.
  populateDeckTiles() {
    const row = document.getElementById('online-deck-row');
    if (!row) return;
    row.innerHTML = '';
    let crestIdx = 0;
    const ids = [];

    const addTile = (deckId, name, raceClass, crestMarkup) => {
      ids.push(deckId);
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = `online-deck-tile ${raceClass}`;
      tile.dataset.deckId = deckId;
      tile.title = name;
      tile.innerHTML = `
        <div class="online-deck-tile-crest">${crestMarkup}</div>
        <div class="online-deck-tile-name">${name}</div>
      `;
      tile.addEventListener('click', () => this.selectDeck(deckId));
      row.appendChild(tile);
      return tile;
    };

    for (const [deckId, deck] of Object.entries(DECKS_DATA)) {
      const raceClass = deck.race === 'Sul' ? 'race-sul' : 'race-human';
      addTile(deckId, deck.name || deckId, raceClass, getFactionCrestMarkup(deck.race, `-online-${crestIdx++}`));
    }

    for (const custom of loadCustomDecks()) {
      const raceClass = custom.race === 'Sul' ? 'race-sul' : 'race-human';
      const customColorId = custom.color || inventory.getEquippedDeckColor();
      const colorStyle = customColorId ? REEL_3_STYLES_MAP.get(customColorId) : null;
      const tile = addTile(custom.id, custom.name, raceClass, '<span class="deck-generic-glyph">◈</span>');
      if (tile && colorStyle) {
        tile.classList.add('has-custom-color');
        tile.style.setProperty('--deck-accent', colorStyle.hex);
        tile.style.setProperty('--deck-accent-glow', `${colorStyle.hex}cc`);
      }
    }

    if (!ids.includes(this.selectedDeckId)) {
      this.selectedDeckId = ids[0] || 'Standard';
    }
    this._syncDeckTileActive();
  }

  _syncDeckTileActive() {
    for (const tile of document.querySelectorAll('.online-deck-tile')) {
      tile.classList.toggle('active', tile.dataset.deckId === this.selectedDeckId);
    }
  }

  selectDeck(deckId) {
    this.selectedDeckId = deckId;
    if (this.game?.deckBuilder && deckId) {
      this.game.deckBuilder._lastSelectedDeckKey = deckId;
    }
    this._syncDeckTileActive();
  }

  // Private Game tile starts collapsed to a single "Private Game" button;
  // tapping it reveals the Create Link / Join split, and Join further
  // reveals the code input - both collapse back here on every openModal().
  resetPrivateTile() {
    const toggle = document.getElementById('online-private-toggle');
    if (toggle) toggle.style.display = '';
    const split = document.getElementById('online-private-split');
    if (split) split.style.display = 'none';
    const form = document.getElementById('online-private-join-form');
    if (form) form.style.display = 'none';
  }

  openPrivateSplit() {
    const toggle = document.getElementById('online-private-toggle');
    if (toggle) toggle.style.display = 'none';
    const split = document.getElementById('online-private-split');
    if (split) split.style.display = 'flex';
  }

  openPrivateJoinForm() {
    const split = document.getElementById('online-private-split');
    if (split) split.style.display = 'none';
    const form = document.getElementById('online-private-join-form');
    if (form) form.style.display = 'flex';
    document.getElementById('online-code-input')?.focus();
  }

  // Resolves the selected deck (preset OR a custom deck built in the deck
  // builder) into a plain card-name array before it ever leaves the
  // browser - the headless game server has no localStorage to look a
  // custom deck's id up in, so sending just the id silently lost custom
  // decks to a fallback preset online (see
  // docs/SCALING_AUDIT_KRITERIENKATALOG.md K-1.3 / B4). `id` rides along
  // only for server-side logging - the server always validates and plays
  // from `cards`.
  _resolveDeckPayload(deckId) {
    const cards = buildNetworkDeckPayload(deckId).map(c => c.name);
    return { id: deckId, cards };
  }

  initWSClient() {
    if (!this.wsClient) {
      this.wsClient = new WSMatchClient({
        onCodeCreated: (code) => {
          this.stopWaitingTimer();
          this.currentCode = code;
          this.brawler?.stop();
          this.resetDuelShareState();
          const duelBtn = document.getElementById('btn-online-duel-share');
          if (duelBtn) duelBtn.style.display = 'none';
          this._syncModeBadge();
          const codeDisplay = document.getElementById('online-code-display');
          if (codeDisplay) {
            codeDisplay.innerText = code;
            codeDisplay.style.display = '';
          }
          const brawlerContainer = document.getElementById('online-brawler-container');
          if (brawlerContainer) brawlerContainer.style.display = 'none';
          const hint = document.getElementById('online-waiting-hint');
          if (hint) {
            hint.innerText = '';
            hint.style.display = 'none';
          }
          const shareActions = document.getElementById('online-waiting-share-actions');
          if (shareActions) shareActions.style.display = 'flex';
          const status = document.getElementById('online-waiting-status');
          if (status) status.innerText = 'Waiting for opponent to join...';
          const fallbackCard = document.getElementById('online-bot-fallback-card');
          if (fallbackCard) fallbackCard.style.display = 'none';
          this.showView('waiting');
          const leaveBtn = document.getElementById('btn-leave-online');
          if (leaveBtn) leaveBtn.style.display = '';

          if (this._pendingDuelShare) {
            this._pendingDuelShare = false;
            this.dispatchDuelInvite(code);
          }
        },
        onQueueWaiting: (position, data) => {
          this.resetDuelShareState();
          const duelBtn = document.getElementById('btn-online-duel-share');
          if (duelBtn) duelBtn.style.display = 'inline-flex';
          this._syncModeBadge();
          const codeDisplay = document.getElementById('online-code-display');
          if (codeDisplay) codeDisplay.style.display = 'none';
          const brawlerContainer = document.getElementById('online-brawler-container');
          if (brawlerContainer) brawlerContainer.style.display = '';
          this.brawler?.start();
          const hint = document.getElementById('online-waiting-hint');
          if (hint) {
            hint.innerText = '';
            hint.style.display = 'none';
          }
          const shareActions = document.getElementById('online-waiting-share-actions');
          if (shareActions) shareActions.style.display = 'none';
          const count = (data && data.position) || (typeof position === 'number' ? position : 1);
          this.showView('waiting');
          const leaveBtn = document.getElementById('btn-leave-online');
          if (leaveBtn) leaveBtn.style.display = '';
          this.startWaitingTimer(count);
        },
        onQueueStatus: (data) => {
          this.isQueueBusy = (data?.status === 'server_busy');
          this._queueBusyMessage = data?.message || 'Server capacity reached. Waiting for an open match slot...';
          const status = document.getElementById('online-waiting-status');
          if (status && this.isQueueBusy) {
            status.innerText = this._queueBusyMessage;
          }
          const hint = document.getElementById('online-waiting-hint');
          if (hint) {
            if (this.isQueueBusy) {
              hint.innerText = 'High server traffic — your position is preserved until a slot opens.';
              hint.style.display = 'block';
            } else {
              hint.innerText = '';
              hint.style.display = 'none';
            }
          }
        },
        onMatchStart: (matchData) => {
          this.resetDuelShareState();
          this.stopWaitingTimer();
          this.brawler?.stop();
          this.closeModal();
          this.game.deckBuilder?.closeModal?.();
          const deckId = this.selectedDeckId || 'Standard';
          this.game.startOnlineMatchWS(this.wsClient, matchData, deckId);
          const leaveBtn = document.getElementById('btn-leave-online');
          if (leaveBtn) leaveBtn.style.display = '';
          if (this._joinCallbacks?.onSuccess) {
            this._joinCallbacks.onSuccess(matchData);
            this._joinCallbacks = null;
          }
        },
        onError: (errMsg) => {
          this.stopWaitingTimer();
          this.brawler?.stop();
          this.setError(errMsg || 'Connection error.');
          if (typeof errMsg === 'string' && /version|reload|balance/i.test(errMsg)) {
            notifyVersionMismatch(errMsg);
          }
          if (this.isOpen) {
            this.showView('choice');
            this.triggerCodeError();
          }
          if (this._joinCallbacks?.onError) {
            this._joinCallbacks.onError(errMsg);
            this._joinCallbacks = null;
          }
        },
        onAuthInvalid: (data) => {
          this.stopWaitingTimer();
          this.brawler?.stop();
          this.setError('');
          if (this.isOpen) {
            this.showView('choice');
          }
          this.showSessionExpiredModal();
        }
      });
    }
    return this.wsClient.connect();
  }

  showSessionExpiredModal(onContinueUnranked = null) {
    const existing = document.getElementById('session-expired-modal');
    if (existing) {
      existing.remove();
      this.game?.overlayGate?.release?.();
    }
    this.game?.overlayGate?.acquire?.();

    const layer = document.createElement('div');
    layer.id = 'session-expired-modal';
    layer.className = 'fullscreen-dialog-overlay active account-overlay';
    layer.style.zIndex = '12050';

    layer.innerHTML = `
      <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
        <div class="fullscreen-dialog-bg-image"></div>
        <div class="fullscreen-dialog-scrim"></div>
        <div class="fullscreen-dialog-vignette"></div>
      </div>
      <div class="fullscreen-dialog-container" style="justify-content: center; align-items: center; min-height: 100vh;">
        <div class="fullscreen-card-panel" style="max-width: 500px; width: 92%; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 16px; padding: clamp(28px, 4vh, 40px) clamp(22px, 3.5vw, 36px); border: 1.5px solid rgba(245, 158, 11, 0.45); box-shadow: 0 0 24px rgba(245, 158, 11, 0.2);">
          <div class="fullscreen-dialog-status-pill status-pill-circular" style="margin-bottom: -2px; border-color: rgba(245, 158, 11, 0.5);">
            <span class="status-indicator-dot" style="background: #fbbf24; box-shadow: 0 0 8px #fbbf24;"></span>
          </div>
          <div class="account-overlay-title" style="font-family: 'Orbitron', sans-serif; font-size: clamp(20px, 2.5vw, 24px); font-weight: 900; letter-spacing: 2px; color: #fbbf24; text-shadow: 0 0 16px rgba(245, 158, 11, 0.45); margin: 0;">
            SESSION EXPIRED
          </div>
          <div class="account-overlay-text" style="font-family: 'Rajdhani', sans-serif; font-size: clamp(15px, 1.8vw, 17px); font-weight: 600; line-height: 1.5; color: #e2e8f0; margin: 0;">
            Your Commander authorization on this device is invalid or has expired (e.g. your account was recovered on another device).
            <br><br>
            Matches played in this state will <strong>NOT</strong> award MMR or count towards your Leaderboard rank.
          </div>
          <div style="display: flex; flex-direction: column; gap: 10px; width: 100%; margin-top: 8px;">
            <button type="button" class="start-btn start-btn-primary" id="btn-expired-recover" style="width: 100%; height: 50px;">
              <span class="start-btn-text">ENTER RECOVERY CODE</span>
            </button>
            <button type="button" class="start-btn start-btn-secondary" id="btn-expired-guest" style="width: 100%; height: 44px; opacity: 0.85;">
              <span class="start-btn-text">PLAY UNRANKED ANYWAY</span>
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(layer);

    let closed = false;
    const closeModal = () => {
      if (closed) return;
      closed = true;
      layer.remove();
      this.game?.overlayGate?.release?.();
    };

    layer.querySelector('#btn-expired-recover')?.addEventListener('click', () => {
      closeModal();
      showRecoverPrompt();
    });

    layer.querySelector('#btn-expired-guest')?.addEventListener('click', () => {
      closeModal();
      if (onContinueUnranked) {
        onContinueUnranked();
      } else if (this._lastAction) {
        this._lastAction();
      }
    });

    layer.addEventListener('click', (e) => {
      if (e.target === layer) closeModal();
    });
  }

  triggerCodeError() {
    this.game?.audio?.playError?.();
    const codeInput = document.getElementById('online-code-input');
    if (codeInput) {
      codeInput.classList.remove('code-error');
      void codeInput.offsetWidth;
      codeInput.classList.add('code-error');
      setTimeout(() => codeInput.classList.remove('code-error'), 1400);
    }
  }

  async create({ allowGuest = false } = {}) {
    this._lastAction = () => this.create({ allowGuest: true });
    const deckId = this.selectedDeckId || 'Standard';
    this.setError('');
    this.showView('connecting');

    try {
      await ensureIdentity().catch(() => {});
      await this.initWSClient();
      this.wsClient.createCode({
        code: '',
        playerName: localStorage.getItem('ucg_player_name') || 'Western Player',
        deck: this._resolveDeckPayload(deckId),
        race: deriveBaseRace(deckId),
        mode: this.mode,
        level: progress.getLevelProgress().level,
        avatarId: progress.getEquippedAvatarId(),
        allowGuest
      });
    } catch (err) {
      this.setError(err.message || 'Could not create match.');
      this.showView('choice');
    }
  }

  async quickJoin({ allowGuest = false } = {}) {
    this._lastAction = () => this.quickJoin({ allowGuest: true });
    const deckId = this.selectedDeckId || 'Standard';
    this.setError('');
    this.showView('connecting');

    try {
      await ensureIdentity().catch(() => {});
      await this.initWSClient();
      this.wsClient.joinQueue({
        playerName: localStorage.getItem('ucg_player_name') || 'Quick Player',
        deck: this._resolveDeckPayload(deckId),
        race: deriveBaseRace(deckId),
        mode: this.mode,
        level: progress.getLevelProgress().level,
        avatarId: progress.getEquippedAvatarId(),
        avatarBorder: inventory.getEquippedAvatarBorder(),
        allowGuest
      });
    } catch (err) {
      this.setError(err.message || 'Could not join matchmaking queue.');
      this.showView('choice');
    }
  }

  startQueue(mode = 'normal', deckId = null) {
    this.openModal();
    if (deckId) {
      this.selectDeck(deckId);
    }
    this.setMode(mode);
    return this.quickJoin();
  }

  async join(explicitCode, options = {}) {
    this._lastAction = () => this.join(explicitCode, { ...options, allowGuest: true });
    const code = (explicitCode || document.getElementById('online-code-input')?.value || '').trim();
    const deckId = this.selectedDeckId || 'Standard';
    this.setError('');

    if (code.length !== 6) {
      this.setError('Please enter a 6-character code.');
      if (this.isOpen) this.triggerCodeError();
      options.onError?.('Please enter a 6-character code.');
      return;
    }

    localStorage.setItem('ucg_last_online_code', code);
    if (this.isOpen) {
      this.showView('connecting');
    }

    this._joinCallbacks = options;

    try {
      await ensureIdentity().catch(() => {});
      await this.initWSClient();
      this.wsClient.joinCode({
        code,
        playerName: localStorage.getItem('ucg_player_name') || 'Eastern Player',
        deck: this._resolveDeckPayload(deckId),
        race: deriveBaseRace(deckId),
        level: progress.getLevelProgress().level,
        avatarId: progress.getEquippedAvatarId(),
        allowGuest: !!options.allowGuest
      });
    } catch (err) {
      this.setError(err.message || 'Failed to join match.');
      if (this.isOpen) {
        this.showView('choice');
        this.triggerCodeError();
      }
      if (this._joinCallbacks?.onError) {
        this._joinCallbacks.onError(err.message || 'Failed to join match.');
        this._joinCallbacks = null;
      }
    }
  }

  copyCode() {
    if (!this.currentCode) return;
    navigator.clipboard?.writeText(this.currentCode).then(() => {
      const btn = document.getElementById('btn-online-copy-code');
      const label = btn?.querySelector('.btn-text');
      if (label) {
        const orig = label.textContent;
        label.textContent = '✓ COPIED!';
        setTimeout(() => { label.textContent = orig; }, 1800);
      }
      this.showLobbyToast('✓ CODE COPIED TO CLIPBOARD!');
    });
  }

  copyLink() {
    if (!this.currentCode) return;
    const url = new URL(window.location.href);
    url.searchParams.set('join', this.currentCode);
    navigator.clipboard?.writeText(url.href).then(() => {
      const btn = document.getElementById('btn-online-copy-link');
      const label = btn?.querySelector('.btn-text');
      if (label) {
        const orig = label.textContent;
        label.textContent = '✓ COPIED!';
        setTimeout(() => { label.textContent = orig; }, 1800);
      }
      this.showLobbyToast('✓ LINK COPIED TO CLIPBOARD!');
    });
  }

  startWaitingTimer(queueCount = 1) {
    this.stopWaitingTimer();
    this.queueStartTime = Date.now();

    const fallbackCard = document.getElementById('online-bot-fallback-card');
    if (fallbackCard) fallbackCard.style.display = 'none';

    const updateStatus = () => {
      if (!this.queueStartTime) return;
      const elapsed = Math.floor((Date.now() - this.queueStartTime) / 1000);
      const mm = Math.floor(elapsed / 60);
      const ss = String(elapsed % 60).padStart(2, '0');
      const timeStr = `${mm}:${ss}`;

      const status = document.getElementById('online-waiting-status');
      const is2v2 = this.mode === '2v2';
      if (status) {
        if (this.isQueueBusy) {
          status.innerText = `Server capacity reached (${timeStr}). Waiting for open slot...`;
        } else {
          status.innerText = is2v2
            ? `Players in queue: ${queueCount}/4 (${timeStr})...`
            : `Searching for opponent (${timeStr})...`;
        }
      }

      // Check fallback threshold (15s) - only for random queue (not private room codes)
      if (!this.currentCode && elapsed >= this.botFallbackThresholdSec) {
        this._revealBotFallback();
      }

      // Pulse hint on duel share button after 10s of searching
      if (!this.currentCode && elapsed >= 10) {
        document.getElementById('btn-online-duel-share')?.classList.add('pulse-hint');
      }
    };

    updateStatus();
    this.waitPollTimer = setInterval(updateStatus, 1000);
  }

  _revealBotFallback() {
    const fallbackCard = document.getElementById('online-bot-fallback-card');
    if (!fallbackCard || fallbackCard.style.display === 'flex') return;

    fallbackCard.style.display = 'flex';
    this.game?.audio?.playUiClick?.();
  }

  stopWaitingTimer() {
    this.isQueueBusy = false;
    this._queueBusyMessage = null;
    if (this.waitPollTimer) {
      clearInterval(this.waitPollTimer);
      this.waitPollTimer = null;
    }
    this.queueStartTime = null;

    const fallbackCard = document.getElementById('online-bot-fallback-card');
    if (fallbackCard) fallbackCard.style.display = 'none';

    document.getElementById('btn-online-duel-share')?.classList.remove('pulse-hint');
  }

  stopWaitingPoll() {
    this.stopWaitingTimer();
  }

  launchBotFallback() {
    this.stopWaitingTimer();
    this.brawler?.stop();

    // Cleanly cancel / leave online queue
    if (this.wsClient) {
      this.wsClient.leaveQueue?.();
      this.wsClient.leave?.();
    }
    this.currentCode = null;

    const leaveBtn = document.getElementById('btn-leave-online');
    if (leaveBtn) leaveBtn.style.display = 'none';

    this.closeModal();
    if (this.game?.deckBuilder) {
      this.game.deckBuilder.closeModal?.();
    }

    if (this.game) {
      const isBlitz = this.mode === 'blitz';
      this.game.speedMultiplier = isBlitz ? 4 : 1;
      const btnSpeed = document.getElementById('btn-speed');
      btnSpeed?.classList.toggle('speed-active', isBlitz);

      const deckId = this.selectedDeckId || 'Standard';
      const enemyDeckId = this.game.getRandomDeckId?.() || 'Standard';

      this.game.audio?.playUiClick?.();
      this.game.startMatch(deckId, enemyDeckId);
    }
  }

  handleCancelClick() {
    const actions = document.getElementById('online-choice-actions');
    if (actions?.classList.contains('confirm-duel')) {
      this.game?.audio?.playUiClick?.();
      this.resetDuelShareState();
      return;
    }
    this.cancelWaiting();
  }

  handleDuelShareClick() {
    const actions = document.getElementById('online-choice-actions');
    const isConfirming = actions?.classList.contains('confirm-duel');

    if (!isConfirming) {
      this.game?.audio?.playUiClick?.();
      actions?.classList.add('confirm-duel');

      clearTimeout(this._duelConfirmTimer);
      this._duelConfirmTimer = setTimeout(() => {
        this.resetDuelShareState();
      }, 6000);
      return;
    }

    clearTimeout(this._duelConfirmTimer);
    this.resetDuelShareState();
    this.game?.audio?.playUiClick?.();
    this.convertToFriendlyDuel();
  }

  resetDuelShareState() {
    clearTimeout(this._duelConfirmTimer);
    document.getElementById('online-choice-actions')?.classList.remove('confirm-duel');
  }

  async convertToFriendlyDuel() {
    this._pendingDuelShare = true;
    this.stopWaitingTimer();
    this.showView('connecting');

    if (this.wsClient) {
      this.wsClient.leaveQueue();
    }

    const deckId = this.selectedDeckId || 'Standard';
    try {
      await ensureIdentity().catch(() => {});
      await this.initWSClient();
      this.wsClient.createCode({
        code: '',
        playerName: localStorage.getItem('ucg_player_name') || 'Western Player',
        deck: this._resolveDeckPayload(deckId),
        race: deriveBaseRace(deckId),
        mode: 'normal',
        level: progress.getLevelProgress().level,
        avatarId: progress.getEquippedAvatarId(),
        allowGuest: true
      });
    } catch (err) {
      this._pendingDuelShare = false;
      this.setError(err.message || 'Could not create duel room.');
      this.showView('choice');
    }
  }

  async dispatchDuelInvite(code) {
    if (!code) return;
    const url = new URL(window.location.href);
    url.searchParams.set('join', code);
    const duelUrl = url.href;

    const isMobileDevice = typeof navigator !== 'undefined' && (
      /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent))
    );

    if (isMobileDevice && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: 'VOID Conflict — Tactical Duel',
          text: `Commander! Challenge me to a tactical duel in VOID Conflict: ${code}`,
          url: duelUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          try {
            await navigator.clipboard?.writeText(duelUrl);
            this.showLobbyToast('✓ DUEL LINK COPIED TO CLIPBOARD!');
          } catch {}
        }
      }
    } else {
      // Desktop (macOS, Windows, Linux): Directly copy without opening any OS share popover
      try {
        await navigator.clipboard?.writeText(duelUrl);
        this.showLobbyToast('✓ DUEL LINK COPIED TO CLIPBOARD!');
      } catch (err) {
        console.warn('Clipboard write failed:', err);
      }
    }
  }

  showLobbyToast(msg) {
    let toast = document.getElementById('online-lobby-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'online-lobby-toast';
      toast.className = 'online-lobby-toast';
      const container = document.getElementById('online-view-waiting') || document.querySelector('.online-modal-box');
      container?.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.remove('show');
    void toast.offsetWidth; // reflow
    toast.classList.add('show');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      toast?.classList.remove('show');
    }, 3200);
  }

  cancelWaiting({ returnToArchive = true } = {}) {
    this.resetDuelShareState();
    this.stopWaitingPoll();
    this.brawler?.stop();
    if (this.wsClient) {
      if (this.currentCode) {
        this.wsClient.cancelCode(this.currentCode);
      }
      this.wsClient.leaveQueue();
      // Aborting a search counts as leaving too - close the connection
      // right away instead of leaving it dangling until the player
      // separately hits the HUD "Leave" button (initWSClient() reconnects
      // fine on the next create/quickJoin/join).
      this.wsClient.leave();
    }
    this.currentCode = null;
    const leaveBtn = document.getElementById('btn-leave-online');
    if (leaveBtn) leaveBtn.style.display = 'none';
    this.closeModal();
    if (returnToArchive) {
      hubNavigator.navigateTo('archive');
    }
  }

  leaveMatch() {
    this.stopWaitingPoll();
    this.brawler?.stop();
    if (this.wsClient && this.currentCode) {
      this.wsClient.cancelCode(this.currentCode);
    }
    this.wsClient?.leaveQueue?.();
    this.currentCode = null;

    const leaveBtn = document.getElementById('btn-leave-online');
    if (leaveBtn) leaveBtn.style.display = 'none';
    this.closeModal();

    if (this.game) {
      this.game.terminateOngoingMatch();
      this.game.hud?.resetForNewMatch?.();
      this.game._closeGameOverModal?.();
      this.game._hidePostGameReturnOverlay?.();
      this.game.cancelTargeting?.();
      this.game.startScreen?.hide?.();
    } else if (this.wsClient) {
      if (this.wsClient.isMatchActive) {
        this.wsClient.sendSurrender?.();
      }
      this.wsClient.leave();
      this.wsClient = null;
    }

    hubNavigator.navigateTo('archive');
    fetchProfile().catch(() => {});
  }
}
